import React, { useState, useEffect } from 'react';
import { PhoneOutlined, HomeOutlined, UserOutlined } from '@ant-design/icons';
import {
  Row,
  Button,
  Col,
  message,
  Form,
  Input,
  Select,
  InputNumber,
} from 'antd';
import moment from 'moment';
import {
  getCities,
  saveRegisterCer,
  createBillingRegisterCertificate,
} from '../service';
import {
  getPaymentMethod,
  createPayment,
} from 'modules/Register/service';
import PaymentMethodList from 'modules/Register/Components/PaymentMethodList';
import {
  filterSelectOptions,
  getErrorMessages,
  isVietnamesePhoneNumber,
} from 'app/utils/functions';

const RegisterReciveCer = ({ exam, examPlan, setRegisterReciveCer }) => {
  const [province, setProvince] = useState({});
  const [step, setStep] = useState(1);
  const [paymentMethodData, setPaymentMethodData] = useState(null);
  const [billingId, setBillingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [form] = Form.useForm();

  const getCitiesData = async (type = '') => {
    const response = await getCities(type);
    if (response.data.code == 200) {
      let cities = response.data.data.cities.map((i) => ({
        value: i.code || i.provinceid,
        label: i.name,
      }));
      cities = cities.filter((item) => Number(item.value) > 0);
      setProvince({ data: cities });
    } else {
      message.error(getErrorMessages(response.data));
    }
  };

  const createBillingRegisterCertificateData = async () => {
    const res = await createBillingRegisterCertificate(examPlan.id);
    if (res.data?.code != 200) {
      message.error(getErrorMessages(res.data));
    }
  };

  const unitPrice = exam?.certificate_price || 50000;

  useEffect(() => {
    getCitiesData(1);
  }, []);

  useEffect(() => {
    if (!examPlan?.billing_register_certificate_id) {
      createBillingRegisterCertificateData();
    }
  }, [examPlan]);

  const onFinish = async (_value) => {
    const name_city_choice = province.data?.find(
      (e) => e.value == _value.province_dctt,
    );
    if (!name_city_choice) {
      return message.error('Vui lòng chọn Tỉnh/TP');
    }

    _value.name_city = name_city_choice.label;
    _value.exam_plan_id = examPlan.id;

    setSubmitting(true);
    const res = await saveRegisterCer(_value);
    setSubmitting(false);

    if (res.data?.code != 200) {
      return message.error(getErrorMessages(res.data));
    }

    const savedBillingId =
      res.data?.data?.billing_id || examPlan.billing_register_certificate_id;

    const pm = await getPaymentMethod();
    if (pm.data.code == 200 && pm.data.data.payment_method_data) {
      setBillingId(savedBillingId);
      setPaymentMethodData(pm.data.data.payment_method_data);
      setStep(2);
    } else {
      message.error('Không tìm thấy phương thức thanh toán');
    }
  };

  const onSelectMethod = async (payment_method_id) => {
    if (!billingId) {
      return message.error('Không tìm thấy hóa đơn thanh toán');
    }

    setPaying(true);
    const result = await createPayment({
      billing_id: billingId,
      payment_method_id,
    });
    setPaying(false);

    if (result.data.code == 200) {
      window.location.href = result.data.data.url;
    } else {
      message.error(getErrorMessages(result.data));
    }
  };

  if (step === 2) {
    return (
      <>
        <PaymentMethodList
          paymentMethodData={paymentMethodData}
          onSelect={paying ? () => {} : onSelectMethod}
          intro="Tiếp tục thanh toán online phí nhận bản cứng chứng chỉ để hoàn thành đăng ký."
          deadlineText={
            exam?.expired_time_receive_cer
              ? `Bạn cần thanh toán trước ${moment(exam.expired_time_receive_cer).format('HH:mm DD/MM/YYYY')} để hoàn thành thủ tục đăng ký.`
              : null
          }
        />
        <Button
          block
          style={{ marginTop: 12 }}
          disabled={paying}
          onClick={() => setStep(1)}
        >
          Quay lại
        </Button>
      </>
    );
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={onFinish}
        initialValues={examPlan.info_receive_cer}
        onValuesChange={(changed, allValues) => {
          if (changed.quantity) {
            const amount = allValues.quantity * unitPrice;
            form.setFieldsValue({ amount });
          }
        }}
      >
        <Row>
          <Col xs={24} className="">
            <Form.Item
              label={<span>tên kỳ thi</span>}
              name="exam_name"
              hidden={true}
              initialValue={exam?.name}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={<span>Họ và Tên</span>}
              name="name"
              normalize={(value) => {
                if (value.trim() === '') {
                  return '';
                }

                return value.toLocaleUpperCase();
              }}
              rules={[{ required: true, message: 'Vui lòng nhập họ tên' }]}
            >
              <Input
                tabIndex={1}
                prefix={<UserOutlined className="site-form-item-icon" />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} className="">
            <Form.Item
              label={<span>Số điện thoại</span>}
              name="phone"
              rules={[
                { required: true, message: 'Vui lòng nhập số điện thoại' },
                () => ({
                  validator(_, value) {
                    if (!value || isVietnamesePhoneNumber(value)) {
                      return Promise.resolve();
                    }

                    return Promise.reject('Số điện thoại không đúng định dạng');
                  },
                }),
              ]}
            >
              <Input
                tabIndex={1}
                type="number"
                prefix={<PhoneOutlined className="site-form-item-icon" />}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label={<span>Tỉnh/TP</span>}
              name="province_dctt"
              rules={[{ required: true, message: 'Vui lòng chọn Tỉnh/TP' }]}
            >
              <Select
                showSearch
                placeholder="Chọn"
                notFoundContent="Không tìm thấy kết quả"
                options={province.data}
                filterOption={filterSelectOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} className="">
            <Form.Item
              label={<span>Địa chỉ nhận giấy chứng nhận</span>}
              name="address"
              normalize={(value) => {
                if (value.trim() === '') {
                  return '';
                }
                return value;
              }}
              rules={[
                {
                  required: true,
                  message: 'Vui lòng nhập địa chỉ nhận giấy chứng nhận',
                },
              ]}
            >
              <Input
                tabIndex={1}
                prefix={<HomeOutlined className="site-form-item-icon" />}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label="Số lượng giấy chứng nhận"
              name="quantity"
              initialValue={1}
              rules={[
                {
                  required: true,
                  message: 'Vui lòng nhập số lượng giấy chứng nhận',
                },
                {
                  type: 'number',
                  min: 1,
                  message: 'Số lượng phải lớn hơn 0',
                },
              ]}
            >
              <InputNumber
                min={1}
                step={1}
                precision={0}
                tabIndex={1}
                style={{ width: '100%' }}
                onKeyDown={(e) => {
                  if (e.key === '.' || e.key === ',' || e.key === '-') {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="amount"
              label="Thành tiền"
              initialValue={unitPrice}
            >
              <InputNumber
                style={{ width: '100%' }}
                disabled
                formatter={(value) =>
                  value ? `${Number(value).toLocaleString('vi-VN')}` : ''
                }
                parser={(value) =>
                  value ? Number(value.replace(/\./g, '')) : 0
                }
                addonAfter="VNĐ"
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Button block onClick={() => setRegisterReciveCer(false)}>
            Hủy
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            style={{ marginTop: '5px' }}
            block
            loading={submitting}
          >
            Tiếp theo
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default RegisterReciveCer;
